import { Box, Stack, Typography, IconButton } from '@mui/material';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';
import { AutoAwesome } from '@mui/icons-material';
import { useGetApiV5FilesIdNamingSuggestions } from '@liscio/api';
import { AICheckButton } from 'components/AI/AIButton/AICheckButton';
import { AutoResizeTextarea } from 'components/AutoResizeTextarea';
import {
  AISuggestionsLoadingState,
  AISuggestionsErrorState,
  AISuggestionsWarningState,
} from 'components/AI/AIFileNameManagement/components/AIFileNameSuggestions/components';
import { removeFileExtension } from 'components/AI/AIFileNameManagement/utils/removeFileExtension';

// Helper function to determine if we should show loading state
const shouldShowLoadingState = (isLoading: boolean, data: any) =>
  isLoading || data?.status === 'processing';

// Check if file has been renamed by checking if original_name exists
const isFileRenamed = (originalName?: string): boolean => Boolean(originalName);
interface FormValues {
  newFileName: string;
  ai_file_name_suggestion_uuid?: string | null;
}

export interface AIFileNameFormProps {
  fileUuid: string;
  onSubmit?: (data: FormValues) => void;
  isLoading?: boolean;
  fileName?: string;
  originalName?: string;
  aiIngestionStatus?: string;
}

const schema = yup.object().shape({
  newFileName: yup
    .string()
    .required('Filename is required')
    .min(3, 'Filename must be at least 3 characters'),
});

export const AIFileNameForm = ({
  fileUuid,
  onSubmit = () => {},
  isLoading = false,
  fileName,
  originalName,
  aiIngestionStatus,
}: AIFileNameFormProps) => {
  const [isManualRename, setIsManualRename] = useState(false);

  const formContextValue = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      newFileName: removeFileExtension(fileName),
      ai_file_name_suggestion_uuid: null,
    },
  });

  // Check if we should make the API call
  const shouldMakeApiCall = () => {
    // Don't make API call if fileUuid is not valid
    if (!fileUuid || fileUuid === 'undefined') return false;
    // Don't make API call if file has been renamed (has original_name)
    if (isFileRenamed(originalName)) return false;
    // Don't make API call if AI ingestion failed
    if (aiIngestionStatus === 'failed') return false;
    // Make API call to fetch AI suggestions
    return true;
  };

  const {
    data,
    isLoading: isSuggestionsLoading,
    error,
    refetch,
  } = useGetApiV5FilesIdNamingSuggestions(fileUuid, {
    query: {
      enabled: shouldMakeApiCall(), // Use the new function to determine if API call should be made
      staleTime: 0, // Always allow fresh data
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
    },
  });

  useEffect(() => {
    // Skip AI suggestions if file has been renamed
    if (isFileRenamed(originalName)) return;

    if (data?.suggestion && data?.suggestion.length > 0 && !isManualRename) {
      const suggestionWithoutExtension = removeFileExtension(data.suggestion);
      formContextValue.setValue('newFileName', suggestionWithoutExtension);
      formContextValue.setValue(
        'ai_file_name_suggestion_uuid',
        data?.ai_file_name_suggestion_uuid ?? null,
      );
    } else if (!isManualRename && !isSuggestionsLoading) {
      // Set default to existing filename when no AI suggestion is available (null, empty, or no data)
      const existingFileName = removeFileExtension(fileName);
      formContextValue.setValue('newFileName', existingFileName);
      formContextValue.setValue('ai_file_name_suggestion_uuid', null);
    }
  }, [
    data,
    isManualRename,
    formContextValue,
    fileName,
    isSuggestionsLoading,
    originalName,
  ]);

  const onFormSubmit = (submitData: FormValues) => {
    onSubmit({
      ...submitData,
      ai_file_name_suggestion_uuid:
        submitData.ai_file_name_suggestion_uuid ?? null,
    });
  };

  const handleRetry = () => {
    refetch();
  };

  const handleRenameManually = () => {
    // Set manual rename mode to show the form field instead of error state
    setIsManualRename(true);
    formContextValue.setValue('ai_file_name_suggestion_uuid', null);
  };

  const handleRetryAISuggestion = () => {
    // Retry the AI suggestion API call
    refetch();
  };

  // Get form context for the input field
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = formContextValue;

  // Watch for changes to newFileName field (needed for external changes from suggestions)
  const newFileNameValue = watch('newFileName');

  // Render the form input field
  const renderFormInput = (hasAISuggestion = false) => (
    <Stack gap={0.625}>
      <Typography variant="body1" fontWeight={600}>
        {hasAISuggestion ? 'AI Proposed Name' : 'File Name'}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1.25}>
        <div
          className={
            errors.newFileName
              ? 'd-block form-group form-error mb-0'
              : 'd-block form-group mb-0'
          }
          style={{ flex: 1, position: 'relative' }}
        >
          <input
            type="hidden"
            {...register('ai_file_name_suggestion_uuid')}
          />
          <AutoResizeTextarea
            {...register('newFileName')}
            className="form-control"
            autoComplete="off"
            placeholder="Enter custom name"
            disabled={isLoading}
            // Pass the watched value to ensure the textarea resizes when changed externally
            value={newFileNameValue}
            onKeyDown={(e) => {
              // We can't use a normal form element here because this component is rendered inside another form
              // Instead, we manually trigger the submit handler when Enter is pressed
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(onFormSubmit)(e);
              }
            }}
          />
          {/* AI magic wand icon - only show when no AI suggestion */}
          {!hasAISuggestion && !isSuggestionsLoading && (
            <IconButton
              size="small"
              onClick={handleRetryAISuggestion}
              disabled={isLoading}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px',
                color: '#999',
              }}
              title="Get AI suggestion"
            >
              <AutoAwesome style={{ fontSize: '16px' }} />
            </IconButton>
          )}
        </div>
        <Box>
          <AICheckButton
            onClick={handleSubmit(onFormSubmit)}
            disabled={isLoading}
          />
        </Box>
      </Stack>
      <span className={errors.newFileName ? 'error_label' : 'd-none'}>
        {errors.newFileName?.message}
      </span>
    </Stack>
  );

  // Determine what to show in place of the form field
  const renderFormField = () => {
    // If user chose to rename manually, always show the form field
    if (isManualRename) {
      return renderFormInput(false); // No AI suggestion when manually renaming
    }

    // If file has been renamed (has original_name), show the form field (no AI suggestions)
    if (isFileRenamed(originalName)) {
      return renderFormInput(false); // No AI suggestion for renamed files
    }

    // If AI ingestion failed, show the form field (no AI suggestions)
    if (aiIngestionStatus === 'failed') {
      return renderFormInput(false); // No AI suggestion when ingestion failed
    }

    // Use real API data
    const currentData = data;
    const currentLoading = isSuggestionsLoading;
    const currentError = error;

    // Loading state - consolidated logic
    if (shouldShowLoadingState(currentLoading, currentData)) {
      return <AISuggestionsLoadingState />;
    }

    // 404 error - No AI suggestions available (yellow warning)
    if (
      currentError &&
      'status' in currentError &&
      currentError.status === 404
    ) {
      return (
        <AISuggestionsWarningState>
          {renderFormInput(false)} {/* No AI suggestion available */}
        </AISuggestionsWarningState>
      );
    }

    // API error (non-404)
    if (currentError) {
      return (
        <AISuggestionsErrorState
          onRetry={handleRetry}
          onRenameManually={handleRenameManually}
        />
      );
    }

    // Note: Processing status is now handled in the consolidated loading check above

    // Default: show the form field with AI suggestion if available
    // If suggestion is null or empty, show standard input without AI label
    const hasAISuggestion = Boolean(currentData?.suggestion && currentData.suggestion.length > 0);
    return renderFormInput(hasAISuggestion);
  };

  return (
    <FormProvider {...formContextValue}>
      <Stack gap={1.25} mt={1.25}>
        {renderFormField()}
      </Stack>
    </FormProvider>
  );
};
